import React from "react";
import PropTypes from "prop-types";
import get from "lodash/get";
import RSInputGroup from "reactstrap/lib/InputGroup";
import InputGroupAddon from "reactstrap/lib/InputGroupAddon";
import InputGroupText from "reactstrap/lib/InputGroupText";

import { gatherClasses } from "../../lib/utils";
import Input from "../input/input";
import InputFeedback from "../inputFeedback/inputFeedback";
import Button from "../button/button";
import Icon from "../icon/icon";
import InputGroupDropdown from "./InputGroupDropdown";
import "./inputGroup.scss";

class InputGroup extends React.Component {
  getComponentTypes = () => {
    const { items } = this.props;
    const prependItems = [];
    const appendItems = [];
    let foundInput = false;
    let input = null;

    items.forEach(item => {
      if (!foundInput && item.component === "input") {
        input = item;
        foundInput = true;
      } else if (!foundInput) {
        prependItems.push(item);
      } else if (foundInput) {
        appendItems.push(item);
      }
    });

    if (input === null) {
      throw new Error("OrionInputGroup requires at least one Input component");
    }

    return { prependItems, input, appendItems };
  };

  getInputGroupComponents = () => {
    const { prependItems, input, appendItems } = this.getComponentTypes();
    const wrappedPrependItems = this.getWrappedItems("prepend", prependItems);
    const wrappedAppendItems = this.getWrappedItems("append", appendItems);
    const inputItem = this.getInputItem(input);

    return {
      wrappedPrependItems,
      inputItem,
      wrappedAppendItems,
      inputObj: input
    };
  };

  getInputItem = input => {
    const { disabled } = this.props;
    const { type = "text" } = input.data || {};

    if (
      ![
        "text",
        "number",
        "email",
        "select",
        "textarea",
        "tel",
        "url",
        "password"
      ].includes(type)
    ) {
      throw new Error(
        "Only input with type text, number, email, select, textarea, tel, url and password are supported"
      );
    }

    return (
      <Input
        {...input.data}
        type={type}
        disabled={disabled || input.data.disabled}
        disableInputFeedback
      />
    );
  };

  getWrappedItems = (addonType, items) => {
    const { disabled } = this.props;
    return items
      .map(({ component, data }, index) => {
        if (
          component === "icon" ||
          component === "text" ||
          component === "button"
        ) {
          if (component === "button") {
            return (
              <InputGroupAddon
                addonType={addonType}
                key={`input-group-${component}-${index}`}
              >
                <Button {...data} disabled={disabled || data.disabled} />
              </InputGroupAddon>
            );
          }

          return (
            <InputGroupAddon
              addonType={addonType}
              key={`input-group-${component}-${index}`}
            >
              <InputGroupText
                className={
                  disabled || data.disabled || data.inactive
                    ? "oui-disabled"
                    : ""
                }
              >
                {component === "icon" ? (
                  React.isValidElement(data) ? (
                    React.cloneElement(data, {
                      disabled
                    })
                  ) : (
                    <Icon {...data} inactive={disabled || data.inactive} />
                  )
                ) : (
                  data.text
                )}
              </InputGroupText>
            </InputGroupAddon>
          );
        }
        if (component === "dropdown") {
          return (
            <InputGroupDropdown
              addonType={addonType}
              {...data}
              disabled={disabled || data.disabled}
              key={`input-group-${component}-${index}`}
            />
          );
        }

        return null;
      })
      .filter(Boolean);
  };

  render() {
    const { wrappedPrependItems, inputItem, inputObj, wrappedAppendItems } =
      this.getInputGroupComponents();
    const { dataTestId } = this.props;

    return (
      <div
        data-testid={dataTestId}
        className={gatherClasses(
          this.props,
          "oui-input-group-wrapper",
          inputObj && inputObj.data.isInvalid ? "oui-input-group-invalid" : ""
        )}
      >
        <RSInputGroup>
          {wrappedPrependItems}
          {inputItem}
          {wrappedAppendItems}
        </RSInputGroup>
        {inputObj &&
          !inputObj.data.disableInputFeedback &&
          inputObj.data.name &&
          inputObj.data.validationProps && (
            <InputFeedback
              show={inputObj.data.isInvalid}
              message={get(
                inputObj.data.validationProps.errors,
                inputObj.data.name
              )}
            />
          )}
      </div>
    );
  }
}

InputGroup.propTypes = {
  disabled: PropTypes.bool,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      component: PropTypes.string.isRequired,
      data: PropTypes.oneOfType([
        PropTypes.shape({
          text: PropTypes.string
        }),
        PropTypes.shape(Input.propTypes),
        PropTypes.shape(Icon.propTypes),
        PropTypes.shape(Button.propTypes),
        PropTypes.shape(InputGroupDropdown.propTypes)
      ]).isRequired
    })
  ).isRequired,
  dataTestId: PropTypes.string
};

InputGroup.defaultProps = {
  dataTestId: "orion-input-group"
};

InputGroup.displayName = "OrionInputGroup";

export default InputGroup;
